<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import RewardsIcon from "@apps/rewards/components/RewardsIcon.svelte";
    import type { RewardsTranslationNamespaces } from "@apps/rewards/constants/constants";
    import { REWARDS_BRANDING_ICONS } from "@apps/rewards/constants/constants";
    import type RewardsAmountFormatter from "@apps/rewards/lib/RewardsAmountFormatter/RewardsAmountFormatter";
    import type RewardsStorefrontValuesService from "@apps/rewards/modules/StorefrontValues/service/RewardsStorefrontValuesService";
    import type RewardsPopUpWidgetLauncherManager from "@apps/rewards/widgets/rewards_popup_widget/utils/RewardsPopUpWidgetLauncherManager/RewardsPopUpWidgetLauncherManager";
    import type { ProductVariantIdManagerInterface } from "@lib/ProductVariantIdManager/ProductVariantIdManagerInterface";
    import type { Observable } from "rxjs";
    import { combineLatest, map, switchMap } from "rxjs";
    import { onMount } from "svelte";

    import Button from "@components/Buttons/Button.svelte";
    import BaseSkeleton from "@components/skeletons/BaseSkeleton.svelte";
    import T from "@components/T.svelte";
    import Text from "@components/Typography/Text.svelte";

    import RewardsEarnPointsQuery from "./queries/RewardsEarnPointsQuery";
    import RewardsEarnPointsService from "./service/RewardsEarnPointsService";

    // props
    export let rewardsEarnPointsService: RewardsEarnPointsService;
    export let rewardsStorefrontValuesService: RewardsStorefrontValuesService;
    export let rewardsEarnPointsQuery: RewardsEarnPointsQuery;
    export let launcherManager: RewardsPopUpWidgetLauncherManager;
    export let rewardsAmountFormatter: RewardsAmountFormatter;
    export let productId: number;
    export let productVariantIdManager: ProductVariantIdManagerInterface;
    export let widgetNamespace: RewardsTranslationNamespaces;
    export let pointsName$: Observable<string>;
    export let afterWidgetInject: () => void;

    const productPointsAmount$ = rewardsEarnPointsQuery.pointsAmount$;
    const variants$ = rewardsEarnPointsQuery.variants$;
    const variantId$ = productVariantIdManager.variantId$;
    const status$ = rewardsEarnPointsQuery.status$;
    const rewardsBranding$ =
        rewardsStorefrontValuesService.selectRewardsBranding();

    const chooseAmount$ = combineLatest([
        variants$,
        variantId$,
        productPointsAmount$,
    ]).pipe(
        map(([variants, variantId, productPointsAmount]) => {
            const variant = variants.find(
                (variant) => variant.variantId === variantId
            );

            if (variant) return variant.pointsAmount;
            return productPointsAmount;
        }),
        switchMap((amount) => rewardsAmountFormatter.format(amount))
    );

    const handleOpenRewardTab = () => {
        launcherManager.rewardsPopUpWidgetShow();
    };

    onMount(() => {
        if (productId) {
            rewardsEarnPointsService.getAmountForProduct(productId);
        }

        afterWidgetInject();
    });
</script>

{#if $rewardsBranding$}
    <div class="gw-rd-earn-points-product-page-widget">
        {#if $status$ === STATUS.LOADING}
            <BaseSkeleton width="100%" height="30px" />
        {:else if $chooseAmount$}
            <div class="gw-rd-earn-points-product-page-widget__content">
                <div
                    class="gw-rd-earn-points-product-page-widget__image-wrapper"
                >
                    <RewardsIcon
                        size="medium"
                        viewBox="0 0 24 24"
                        isBrandingIconColor={false}
                    >
                        <svelte:component
                            this={REWARDS_BRANDING_ICONS[
                                $rewardsBranding$.popUpWidget.launcher.icon.type
                            ]}
                        />
                    </RewardsIcon>
                </div>
                <div>
                    <Text component="span" variant="base">
                        <T
                            namespace={widgetNamespace}
                            translationKey="earn_points_on_purchase"
                            translationOptions={{
                                points: $chooseAmount$,
                                points_name: $pointsName$,
                            }}
                        />
                    </Text>
                    <Button variant="link" on:click={handleOpenRewardTab}>
                        <Text component="span" variant="base">
                            <T
                                namespace={widgetNamespace}
                                translationKey="learn_more"
                            />
                        </Text>
                    </Button>
                </div>
            </div>
        {/if}
    </div>
{/if}

<style lang="postcss">
    .gw-rd-earn-points-product-page-widget {
        &__content {
            display: flex;
            align-items: flex-start;
            column-gap: var(--gw-rd-space-4);
            border-radius: var(--gw-rd-border-radius-xs);
            padding: var(--gw-rd-space-5);
            font-size: 16px;
            background-color: var(--gw-background-color);
            color: var(--gw-font-color);
        }

        &__image-wrapper {
            display: var(--gw-icon-display);
            color: var(--gw-icon-color);
            align-items: center;
        }

        :global(.gw-btn.gw-button.--link) {
            font-size: 16px;
            color: var(--gw-font-color);
            text-decoration-color: var(--gw-font-color);
        }
    }
</style>
